import { Component, Input, Output, EventEmitter, HostBinding, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, AfterViewInit, ElementRef, ViewChild, Provider, Optional } from '@angular/core';
import { Button } from '@js/primeng17/button';
import { PRODUCT_LABEL } from '../product-label';
import { ActivatedRoute, Router } from '@angular/router';
import { A11yStatusService } from '../../helper/services/a11ystatus.service';
import { UiTranslateService } from '../../helper/lang';

const providers: Provider[] = [];
declare type ButtonIconPosition = 'left' | 'right' | 'top' | 'bottom';

@Component({
    selector: 'ig-button',
    template: `
    <button
      [attr.data-btn-e2e]="e2e"
      [attr.type]="type"
      [class]="styleClass"
      [ngStyle]="style"
      [disabled]="disabled"
      [attr.aria-disabled]="disabled || fakeDisabled"
      [attr.aria-label]="ariaLabel || label"
      [ngClass]="{
        'p-button p-component': true,
        'p-button-icon-only': icon && iconOnly,
        'p-button-sm': size === 'small',
        'p-button-lg': size === 'large',
        'p-button-secondary p-button-outlined': mode === 'secondary',
        'p-button-success': mode === 'cta',
        'p-button-danger': mode === 'danger',
        'p-button-loading': loading,
        'p-button-disabled': disabled || fakeDisabled,
        'p-button-fake-disabled': fakeDisabled,
        'p-button-vertical': (iconPos === 'top' || iconPos === 'bottom') && !iconOnly
      }"
      (igA11yLink)="myOnClick($event)"
      (focus)="onFocus.emit($event)"
      (blur)="onBlur.emit($event)"
      #buttonEl
      pRipple
    >
      <ng-content></ng-content>
      <ng-container *ngIf="contentTemplate">
        <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
      </ng-container>
      <span
        [ngClass]="{
          'p-button-icon': true,
          'p-button-icon-left': iconPos === 'left' && !iconOnly,
          'p-button-icon-right': iconPos === 'right' && !iconOnly,
          'p-button-icon-top': iconPos === 'top' && !iconOnly,
          'p-button-icon-bottom': iconPos === 'bottom' && !iconOnly
        }"
        [class]="icon"
        *ngIf="icon"
        [attr.aria-hidden]="true"
      ></span>
      <span class="p-button-label" *ngIf="!iconOnly">{{ label }}</span>
      <ig-product-label *ngIf="productLabel" [label]="productLabel" style="margin-left: 4px"></ig-product-label>
      <span [ngClass]="badgeStyleClass()" *ngIf="badge" [class]="badgeClass || ''">{{ badge }}</span>
    </button>
  `,
    providers: providers,
    styleUrls: ['./button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: (Button.prototype.constructor as any).__annotations__ &&
        (Button.prototype.constructor as any).__annotations__.length &&
        (Button.prototype.constructor as any).__annotations__.animations
        ? (Button.prototype.constructor as any).__annotations__[0].animations
        : [],
    standalone: false
})
export class ButtonComponent extends Button implements OnChanges, AfterViewInit {
  @HostBinding('attr.data-e2e-mode') e2eMode = 'primary';
  @ViewChild('buttonEl') buttonEl?: ElementRef<HTMLButtonElement>;

  /**
   * is button loading?
   */
  @Input() loading = false;

  /**
   * Add e2e tag for Buttons
   */
  @HostBinding('attr.data-e2e')
  @Input() e2e?: string;

  /**
   * type of button (button, submit, reset, search)
   */
  @Input() type = 'button';

  /**
   * modes of button (primary,cta, secondary, )
   */
  mode = 'primary';
  @Input('mode') set _mode(value: string) {
    this.mode = value;
    this.e2eMode = value;
  }

  /**
   * type of button (default, big, small)
   */
  @Input() size: "small" | "large" | undefined;

  /**
   * position of the icon
   */
  @Input() override iconPos: ButtonIconPosition = 'left';

  /**
   * icon class (from primeicons or fontawesome)
   */
  @Input() icon = '';

  /**
   * do not display the label, only the icon
   */
  @Input() iconOnly = false;

  /**
   * label of the button (you must provide one, even if it is an icon-only button. The label is used for a11y then)
   */
  @Input({ required: true }) label = '';

  /**
   * is button disabled?
   */
  @Input() disabled = false;

  /**
   * fake disable button (only applies disabled class, button is still clickable)
   */
  @Input() fakeDisabled?: boolean;

  /**
   * inline style rules for the button
   */
  @Input() style = {};

  /**
   * style classes for the button (used for the severities etc.)
   */
  @Input() styleClass = '';

  /**
   * sets the focus to this button
   */
  @Input() autoFocus = false;

  /**
   * optional <ig-product-label> to display next to button label
   */
  @Input() productLabel?: PRODUCT_LABEL;

  /**
   * @deprecated don't use routerLink directive on ig-button. It will add a tabindex to the ig-button module so it is focusable, but nothing happens on enter as the inner <button> is not affected. This is an a11y issue. Use the `buttonRouterLink` attribute instead
   * this is only to warn the developer to not use routerLink on ig-button
   */
  @Input() routerLink?: any;

  /**
   * equivalent of using routerLink but will not add extra tabindex to the ig-button element (a11y)
   */
  @Input() buttonRouterLink?: string[];

  /**
   * emitted on button click
   */
  @Output() override onClick: EventEmitter<any> = new EventEmitter();

  /**
   * emitted on focus in
   */
  @Output() override onFocus: EventEmitter<any> = new EventEmitter();

  /**
   * emitted on focus out
   */
  @Output() override onBlur: EventEmitter<any> = new EventEmitter();

  noop() { }

  private announcedLoading = false;
  private announcedLoadingTimeout?: number;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly elem: ElementRef,
    private readonly router: Router,
    @Optional() private readonly route: ActivatedRoute,
    private readonly a11yStatusService: A11yStatusService,
    private readonly translate: UiTranslateService,
  ) {
    super(elem);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['loading']) {
      if (this.announcedLoadingTimeout) {
        window.clearTimeout(this.announcedLoadingTimeout);
      }
      this.announcedLoadingTimeout = window.setTimeout(() => {
        if (this.loading) {
          this.announcedLoading = true;
          this.a11yStatusService.announce(this.translate.instant('loading_status'));

        } else if (this.announcedLoading) {
          this.announcedLoading = false;
          this.a11yStatusService.announce(this.translate.instant('loading_ready'));
        }

      }, 100);
    }

    if (changes['loading'] || changes['icon'] || changes['label'] || changes['disabled'] || changes['style'] || changes['styleClass']) {
      this.ref.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    if (this.routerLink) {
      console.warn('Do not use `routerLink` directive on ig-button. It is an a11y issue. Use the `buttonRouterLink` property instead');
    }

    // check for native `disabled` attribute once and set the internal `disabled` attribute then
    if (this.elem.nativeElement && (this.elem.nativeElement as HTMLElement).hasAttribute('disabled')) {
      this.disabled = true;
      this.ref.markForCheck();
    }

    if (this.autoFocus) {
      window.setTimeout(() => {
        if (this.buttonEl?.nativeElement) {
          this.buttonEl.nativeElement.focus();
        }
      }, 100);
    }
  }

  myOnClick(event: Event) {
    if (!this.disabled) {
      if (this.buttonRouterLink) {
        this.router.navigate(this.buttonRouterLink, { relativeTo: this.route });
      }
      this.onClick.emit(event);
    }
  }
}
